import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronRight, LogOut, PanelLeftClose, PanelLeft } from 'lucide-react';
import type { NavGroup } from '@/config/navigation';
import { useFeatureModule } from '@/hooks/useFeatureModule';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore, type AuthenticatedUser } from '@/stores/authStore';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useNameFormat } from '@/utils/nameFormat';
import { countBadge, countBadgeTiny } from '@/styles/badges';

interface DesktopDrawerProps {
  navGroups: NavGroup[];
  user: AuthenticatedUser | null;
  onLogout?: () => void;
}

const DRAWER_WIDTH_EXPANDED = 224;
const DRAWER_WIDTH_COLLAPSED = 60;

/**
 * Desktop Navigation Drawer
 *
 * Persistent sidebar for desktop navigation that can be collapsed to icons-only.
 * Uses useUIStore for collapse state management (persisted to localStorage).
 *
 * Features:
 * - Smooth width transition
 * - Grouped navigation with icons
 * - Active link highlighting
 * - User info section with profile link
 * - Collapse toggle at bottom
 * - Module-based filtering
 */
export const DesktopDrawer: React.FC<DesktopDrawerProps> = ({ navGroups, user, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { isDrawerCollapsed, toggleDrawer } = useUIStore();
  const { viewMode } = useAuthStore();
  const { formatStaffName, formatParentName } = useNameFormat();
  const primaryColor = 'var(--primary-color)';
  const isParent = user?.role === 'parent';

  // Format user's display name based on their role
  const formatUserName = (firstName: string, lastName: string) => {
    if (isParent) {
      return formatParentName(firstName, lastName);
    }
    return formatStaffName(firstName, lastName);
  };

  // Feature module status for filtering
  const { isEnabled: attendanceEnabled } = useFeatureModule('attendance');
  const { isEnabled: parentPortalEnabled } = useFeatureModule('parentPortal');
  // Intentional Layer-1-only: SMS usage/billing stays visible when the school pauses SMS via sms_enabled
  const { isAllowed: smsEnabled } = useFeatureModule('sms');
  const { isEnabled: messagingEnabled } = useFeatureModule('messaging');

  const moduleStatus: Record<'attendance' | 'parentPortal' | 'sms' | 'messaging', boolean> = {
    attendance: attendanceEnabled,
    parentPortal: parentPortalEnabled,
    sms: smsEnabled,
    messaging: messagingEnabled,
  };

  // Filter groups based on module access
  const visibleNavGroups = navGroups
    .filter((group) => !group.requiresModule || moduleStatus[group.requiresModule])
    .map((group) => ({
      ...group,
      links: group.links.filter((link) => !link.requiresModule || moduleStatus[link.requiresModule]),
    }))
    .filter((group) => group.links.length > 0);

  // Find which nav group contains the current pathname (strip query params from link.to)
  const findActiveGroupIndex = (pathname: string) =>
    visibleNavGroups.findIndex((group) =>
      group.links.some((link) => {
        const linkPath = link.to.split('?')[0];
        return pathname === linkPath || pathname.startsWith(linkPath + '/');
      })
    );

  // Track which sections are expanded (by group index)
  // Admins in admin view: auto-expand the active group, others: expanded by default
  const [expandedSections, setExpandedSections] = useState<Set<number>>(() => {
    if (user?.role === 'admin' && viewMode === 'admin') {
      const activeIndex = findActiveGroupIndex(location.pathname);
      return activeIndex >= 0 ? new Set([activeIndex]) : new Set();
    }
    return new Set(visibleNavGroups.map((_, i) => i)); // All expanded for other roles
  });

  // Update expanded sections when viewMode changes (for admins switching between views)
  // Note: visibleNavGroups intentionally excluded - it's recreated each render and we only care about viewMode changes
  useEffect(() => {
    if (user?.role === 'admin') {
      if (viewMode === 'admin') {
        const activeIndex = findActiveGroupIndex(location.pathname);
        setExpandedSections(activeIndex >= 0 ? new Set([activeIndex]) : new Set());
      } else {
        setExpandedSections(new Set(visibleNavGroups.map((_, i) => i))); // Expand all
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  // Auto-expand the active group on route changes (admin view only)
  useEffect(() => {
    if (user?.role === 'admin' && viewMode === 'admin') {
      const activeIndex = findActiveGroupIndex(location.pathname);
      if (activeIndex >= 0) {
        setExpandedSections((prev) => {
          if (prev.has(activeIndex)) return prev;
          return new Set(prev).add(activeIndex);
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  // In collapsed mode, clicking a group icon expands drawer AND that section
  const handleCollapsedGroupClick = (groupIndex: number) => {
    toggleDrawer(); // Expand drawer
    setExpandedSections((prev) => new Set(prev).add(groupIndex)); // Ensure this section is open
  };

  const drawerWidth = isDrawerCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;

  const drawerStyle: React.CSSProperties = {
    width: drawerWidth,
    minWidth: drawerWidth,
    backgroundColor: 'var(--surface-color)',
    borderRight: '1px solid var(--border-color)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease, min-width 0.2s ease',
    overflow: 'hidden',
    position: 'sticky',
    top: 'var(--header-height)',
    height: 'calc(100vh - var(--header-height))',
    maxHeight: 'calc(100vh - var(--header-height))',
  };

  // Nav list scrolls independently, bottom section stays fixed
  const navListStyle: React.CSSProperties = {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: '0.5rem',
  };

  // Bottom section stays fixed at bottom (outside scroll container)
  const bottomSectionStyle: React.CSSProperties = {
    flexShrink: 0,
    backgroundColor: 'var(--surface-color)',
  };

  const groupHeaderStyle: React.CSSProperties = {
    width: '100%',
    padding: isDrawerCollapsed ? '1rem 0.5rem' : '1rem 1rem 0.5rem',
    fontSize: '0.7rem',
    fontWeight: '700',
    color: 'var(--text-tertiary)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    justifyContent: isDrawerCollapsed ? 'center' : 'flex-start',
    gap: '0.5rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    transition: 'background-color 0.15s ease',
  };

  const chevronStyle: React.CSSProperties = {
    fontSize: '0.6rem',
    marginLeft: 'auto',
    transition: 'transform 0.15s ease',
  };

  const getNavLinkStyle = (linkTo: string): React.CSSProperties => {
    const hasQuery = linkTo.includes('?');
    let isActive: boolean;
    if (hasQuery) {
      const [linkPath, linkQuery] = linkTo.split('?');
      const fullLocation = location.pathname + location.search;
      // Match full path+search, OR when on the base path with no search params, treat inbox as active
      isActive = fullLocation === linkTo || (location.pathname === linkPath && !location.search && linkQuery?.includes('tab=inbox'));
    } else {
      isActive = location.pathname === linkTo;
    }
    return {
      display: 'flex',
      alignItems: 'center',
      padding: '0.5rem 1rem 0.5rem 2.5rem', // Indented for child items
      color: isActive ? 'var(--primary-color)' : 'var(--text-primary)',
      textDecoration: 'none',
      borderLeft: isActive ? '3px solid var(--primary-color)' : '3px solid transparent',
      backgroundColor: isActive ? 'var(--background-color)' : 'transparent',
      fontWeight: isActive ? '500' : 'normal',
      transition: 'all 0.15s ease',
      fontSize: '0.875rem',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    };
  };

  const iconStyle: React.CSSProperties = {
    fontSize: '1.1rem',
    flexShrink: 0,
    width: '24px',
    textAlign: 'center',
  };

  const userSectionStyle: React.CSSProperties = {
    borderTop: '1px solid var(--border-color)',
    padding: isDrawerCollapsed ? '0.75rem 0.5rem' : '0.75rem 1rem',
    backgroundColor: 'var(--background-color)',
  };

  const userRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    marginBottom: onLogout ? '0.5rem' : 0,
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: 'var(--radius)',
    transition: 'background-color 0.2s',
    justifyContent: isDrawerCollapsed ? 'center' : 'flex-start',
  };

  const userAvatarStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: 'var(--primary-color)',
    color: 'var(--on-primary-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.875rem',
    fontWeight: '600',
    flexShrink: 0,
  };

  const logoutButtonStyle: React.CSSProperties = {
    width: isDrawerCollapsed ? '36px' : '100%',
    height: isDrawerCollapsed ? '36px' : 'auto',
    padding: isDrawerCollapsed ? '0' : '0.5rem 0.75rem',
    backgroundColor: 'transparent',
    color: 'var(--error-color)',
    border: '1px solid var(--error-color)',
    borderRadius: 'var(--radius)',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.8125rem',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
  };

  const getUserInitials = (): string => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || '?';
  };

  return (
    <nav style={drawerStyle} aria-label="Navigation sidebar" id="desktop-nav-drawer">
      {/* Navigation Groups - scrolls independently */}
      <div style={navListStyle} className="sidebar-scroll">
        {visibleNavGroups.map((group, groupIndex) => {
          const isExpanded = expandedSections.has(groupIndex);

          return (
            <div key={groupIndex}>
              {/* Group Header - clickable to expand/collapse */}
              <button
                style={groupHeaderStyle}
                onClick={() => {
                  if (isDrawerCollapsed) {
                    handleCollapsedGroupClick(groupIndex);
                  } else {
                    toggleSection(groupIndex);
                  }
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--background-color)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title={isDrawerCollapsed ? group.title : undefined}
                aria-expanded={isExpanded}
                aria-controls={`nav-section-${groupIndex}`}
              >
                {group.icon && (
                  <span style={{ ...iconStyle, position: 'relative' }}>
                    <group.icon size={18} aria-hidden="true" style={{ color: primaryColor }} />
                    {isDrawerCollapsed && group.badge != null && group.badge > 0 && (
                      <span
                        style={{
                          ...countBadgeTiny,
                          position: 'absolute',
                          top: '-6px',
                          right: '-8px',
                        }}
                      >
                        {group.badge > 99 ? '99+' : group.badge}
                      </span>
                    )}
                  </span>
                )}
                {!isDrawerCollapsed && (
                  <>
                    <span style={{ flex: 1, textAlign: 'left' }}>{group.title}</span>
                    {!isExpanded && group.badge != null && group.badge > 0 && (
                      <span style={countBadge}>
                        {group.badge > 99 ? '99+' : group.badge}
                      </span>
                    )}
                    <span style={chevronStyle}>
                      {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                    </span>
                  </>
                )}
              </button>

              {/* Child Links - only visible when expanded AND drawer is not collapsed */}
              {isExpanded && !isDrawerCollapsed && (
                <div id={`nav-section-${groupIndex}`} role="region">
                  {group.links.map((link) => {
                    const hasQuery = link.to.includes('?');
                    const [linkPath, linkQuery] = link.to.split('?');
                    const fullLocation = location.pathname + location.search;
                    const isCurrent = hasQuery
                      ? fullLocation === link.to || (location.pathname === linkPath && !location.search && linkQuery?.includes('tab=inbox'))
                      : location.pathname === link.to;
                    return (
                      <Link
                        key={link.to}
                        to={link.to}
                        style={getNavLinkStyle(link.to)}
                        aria-current={isCurrent ? 'page' : undefined}
                      >
                        {link.label}
                        {link.badge != null && link.badge > 0 && (
                          <span
                            style={{ ...countBadge, marginLeft: 'auto' }}
                          >
                            {link.badge > 99 ? '99+' : link.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom Section - User + Controls (fixed at bottom, outside scroll) */}
      <div style={bottomSectionStyle}>
        {/* User Section */}
        {user && (
          <div style={userSectionStyle}>
            {/* User row: Avatar + Name/Role */}
            <div
              style={userRowStyle}
              onClick={() => navigate('/profile')}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate('/profile');
                }
              }}
              title={isDrawerCollapsed ? `${user.firstName || user.email} - View profile` : 'View profile'}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--surface-color)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              {/* Avatar */}
              <div style={userAvatarStyle}>{getUserInitials()}</div>

              {/* Name and Role (hidden when collapsed) */}
              {!isDrawerCollapsed && (
                <div style={{ overflow: 'hidden' }}>
                  <div
                    style={{
                      fontWeight: '500',
                      color: 'var(--text-primary)',
                      marginBottom: '0.125rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {user.firstName && user.lastName ? formatUserName(user.firstName, user.lastName) : user.email}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
                    {user.role === 'parent' ? t('roles.parent') : user.role.replace('_', ' ')}
                  </div>
                </div>
              )}
            </div>

            {/* Theme Toggle - only shown here when collapsed */}
            {isDrawerCollapsed && (
              <div
                style={{
                  marginBottom: '0.5rem',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <ThemeToggle compact />
              </div>
            )}

            {/* Logout button */}
            {onLogout && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: isDrawerCollapsed ? 'center' : 'flex-start',
                }}
              >
                <button
                  style={logoutButtonStyle}
                  onClick={onLogout}
                  title={t('logout')}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--error-color)';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = 'var(--error-color)';
                  }}
                >
                  {isDrawerCollapsed ? <LogOut size={16} aria-hidden="true" /> : t('logout')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Collapse Toggle Section */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: isDrawerCollapsed ? 'center' : 'space-between',
            padding: '0.25rem 0.75rem',
            borderTop: '1px solid var(--border-color)',
          }}
        >
          {/* Theme Toggle - shown here when expanded */}
          {!isDrawerCollapsed && <ThemeToggle compact style={{ width: '28px', height: '28px' }} />}

          <button
            style={{
              padding: '0.5rem',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              color: 'var(--text-secondary)',
              transition: 'all 0.2s',
              borderRadius: 'var(--radius)',
            }}
            onClick={toggleDrawer}
            aria-expanded={!isDrawerCollapsed}
            aria-controls="desktop-nav-drawer"
            title={isDrawerCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--surface-color)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            {isDrawerCollapsed ? (
              <PanelLeft size={20} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={20} aria-hidden="true" />
            )}
            {!isDrawerCollapsed && <span style={{ marginLeft: '0.5rem', fontSize: '0.8125rem' }}>{t('nav.collapse')}</span>}
          </button>
        </div>
      </div>
    </nav>
  );
};
